<template>
  <div>
    <!-- 遮罩层 -->
    <div class="canvas-modal-overlay" v-show="visibleCanvas" @click="closeModal">
      <!-- 漂浮窗主体 -->
      <div class="canvas-modal" @click.stop v-loading="loading">
        <!-- 关闭按钮 -->
        <div class="canvas-close-btn" @click="closeModal">&times;</div>
        <!-- 漂浮窗内容 -->
        <div class="canvas-modal-body">
          <!-- 画笔大小控制条（替代搜索栏位置） -->
          <div class="brush-size-bar">
            <label class="brush-label">
              <span class="brush-icon"></span>
              画笔大小
            </label>
            <div class="brush-slider">
              <el-slider v-model="brushSize" :max="300" :show-tooltip="false"></el-slider>
            </div>
            <span class="brush-value">{{ formattedBrushSize }}px</span>
          </div>

          <!-- 操作按钮组（替代筛选按钮位置） -->
          <div class="canvas-controls">
            <div class="control-btn" :class="{active: !isErasing}" @click="toggleEraser">
              <span class="btn-icon"></span>
              {{ isErasing ? '画笔模式' : '画笔模式' }}
            </div>
            <div class="control-btn" :class="{active: isErasing}" @click="toggleEraser">
              <span class="btn-icon"></span>
              {{ isErasing ? '橡皮擦' : '橡皮擦' }}
            </div>
            <div class="control-btn" @click="getImage">
              <span class="btn-icon"></span>
              重置画布
            </div>
            <div class="control-btn delete-btn" v-show="originalImage" @click="clearImage">
              <span class="btn-icon"></span>
              清空画布
            </div>
          </div>

          <!-- 画布区域 -->
          <div class="canvas-board">
            <!-- 背景层：显示原图（不可编辑） -->
            <canvas ref="canvasBackground" class="canvas-background" width="1280" height="1175"></canvas>
            <!-- 前景层：绘制黑色遮罩 -->
            <canvas ref="canvasForeground" class="canvas-foreground" width="1280" height="1175"
              @mousedown="startDrawing"
              @mousemove="handleMouseMove"
              @mouseup="stopDrawing"
              @mouseleave="handleMouseLeave">
            </canvas>
            <!-- 自定义画笔光标 -->
            <div class="brush-cursor" ref="brushCursor" v-show="showCursor"
              :style="{
                width: cursorSize + 'px',
                height: cursorSize + 'px',
                left: cursorX + 'px',
                top: cursorY + 'px'
              }">
            </div>
          </div>

          <!-- 底部保存/取消按钮 -->
          <div class="canvas-actions">
            <button class="action-save-btn" @click="saveDrawing">
              <span class="btn-icon"></span>
              保存遮罩
            </button>
            <button class="action-cancel-btn" @click="closeModal">
              <span class="btn-icon"></span>
              取消
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { notification } from '@/utils/notification'
import { getOssClient } from '@/utils/oss'
export default {
  name: 'CanvasPad',
  props: {
    visibleCanvas: {
      type: Boolean,
      default: false
    },
    imagePath: {
      type: String,
      default: ''
    },
    // 已有遮罩图（含原图+黑色遮罩的合成图），打开时回填到前景层
    maskPath: {
      type: String,
      default: ''
    }
  },
  watch: {
    imagePath: function (val) {
      if (val) {
        this.currentImage = val
        this.$nextTick(() => {
          console.log('canvas mounted')
          this.getImage()
        })
      }
    },
    // 弹窗每次打开时，若存在 maskPath 则尝试回填遮罩
    visibleCanvas: function (val) {
      if (val && this.maskPath) {
        this.$nextTick(() => {
          // 等背景层先加载完成再叠加遮罩
          setTimeout(() => this.loadExistingMask(), 300)
        })
      }
    },
    brushSize: function (val) {
      // 画笔大小变化时同步更新光标大小
      this.cursorSize = val
    }
  },
  data() {
    return {
      ctxBackground: null, // 背景层context
      ctxForeground: null, // 前景层context（遮罩层）
      originalImage: null, // 原始图像数据
      currentImage: null, // 当前图像数据（可能包含涂鸦）
      isDrawing: false,
      isErasing: false, // 是否处于橡皮擦模式
      brushSize: 100, // 默认画笔大小
      loading: true,
      // 自定义光标
      showCursor: false,
      cursorX: 0,
      cursorY: 0,
      cursorSize: 100
    }
  },
  created() {
  },
  mounted() {
    // 添加按键监听事件
    window.addEventListener('keydown', this.handleKeyclose)
    this.$nextTick(() => {
      if (this.$refs.canvasBackground && this.$refs.canvasForeground) {
        this.ctxBackground = this.$refs.canvasBackground.getContext('2d')
        this.ctxForeground = this.$refs.canvasForeground.getContext('2d')
        this.$refs.canvasBackground.width = 1280
        this.$refs.canvasBackground.height = 1175
        this.$refs.canvasForeground.width = 1280
        this.$refs.canvasForeground.height = 1175
        this.getImage()
      }
    })
  },
  methods: {
    // 退出按钮
    handleKeyclose(event) {
      if (event.key === 'Escape') {
        this.closeModal()
      }
      if (event.shiftKey && event.key === 'E') {
        this.closeModal()
      }
    },
    toggleEraser() {
      this.isErasing = !this.isErasing
      if (this.ctxForeground) {
        if (this.isErasing) {
          // 橡皮擦模式：使用destination-out来擦除前景层内容
          this.ctxForeground.globalCompositeOperation = 'destination-out'
        } else {
          // 画笔模式：正常绘制黑色
          this.ctxForeground.globalCompositeOperation = 'source-over'
        }
      }
    },
    draw(e) {
      if (!this.isDrawing) return
      // 获取 canvas 实际绘制区域的尺寸
      const rect = this.$refs.canvasForeground.getBoundingClientRect()
      const scaleX = this.$refs.canvasForeground.width / rect.width
      const scaleY = this.$refs.canvasForeground.height / rect.height

      const x = Math.round((e.clientX - rect.left) * scaleX)
      const y = Math.round((e.clientY - rect.top) * scaleY)

      this.ctxForeground.lineTo(x, y)
      // 画笔模式绘制黑色，橡皮擦模式使用destination-out擦除
      this.ctxForeground.strokeStyle = this.isErasing ? 'rgba(0,0,0,1)' : 'black'
      this.ctxForeground.lineWidth = this.brushSize
      this.ctxForeground.lineCap = 'round'
      this.ctxForeground.lineJoin = 'round' // 添加圆角连接,避免尖角
      this.ctxForeground.stroke()
    },
    // 处理鼠标移动（同时更新光标和绘制）
    handleMouseMove(e) {
      const board = this.$refs.canvasForeground.parentElement
      const boardRect = board.getBoundingClientRect()
      const canvas = this.$refs.canvasForeground
      const canvasRect = canvas.getBoundingClientRect()
      // 计算 canvas 的缩放比例
      const scaleX = canvasRect.width / canvas.width
      // 根据缩放比例调整光标大小
      const displaySize = this.brushSize * scaleX
      this.cursorSize = displaySize
      // 更新光标位置（相对于 canvas-board）
      this.cursorX = e.clientX - boardRect.left - displaySize / 2
      this.cursorY = e.clientY - boardRect.top - displaySize / 2
      this.showCursor = true
      // 继续执行绘制逻辑
      this.draw(e)
    },
    // 处理鼠标离开画布
    handleMouseLeave() {
      this.showCursor = false
      this.stopDrawing()
    },
    // 处理打开图片
    getImage() {
      const image = new Image()
      image.crossOrigin = 'Anonymous'
      image.src = this.imagePath
      image.onload = () => {
        if (!this.$refs.canvasBackground || !this.$refs.canvasForeground) {
          return
        }
        const canvasBackground = this.$refs.canvasBackground
        const canvasForeground = this.$refs.canvasForeground
        // 确保 context 已初始化
        if (!this.ctxBackground) {
          this.ctxBackground = canvasBackground.getContext('2d')
        }
        if (!this.ctxForeground) {
          this.ctxForeground = canvasForeground.getContext('2d')
        }

        // 检查 context 是否有效
        if (!this.ctxBackground || !this.ctxForeground) {
          console.error('Canvas context is null')
          this.loading = false
          return
        }

        // 设置两个canvas的尺寸与图片完全一致
        canvasBackground.width = image.width
        canvasBackground.height = image.height
        canvasForeground.width = image.width
        canvasForeground.height = image.height

        // 背景层：绘制原图
        this.ctxBackground.clearRect(0, 0, canvasBackground.width, canvasBackground.height)
        this.ctxBackground.drawImage(image, 0, 0, image.width, image.height)

        // 前景层：清空为全透明，准备绘制遮罩
        this.ctxForeground.clearRect(0, 0, canvasForeground.width, canvasForeground.height)
        // 重置合成模式为正常模式
        this.ctxForeground.globalCompositeOperation = 'source-over'
        this.isErasing = false

        // 保存原始图像数据
        this.originalImage = this.ctxBackground.getImageData(0, 0, canvasBackground.width, canvasBackground.height)
        this.loading = false
      }
      image.onerror = () => {
        console.error('Image load failed:', this.imagePath)
        this.loading = false
      }
    },
    // 从已有遮罩合成图中提取黑色像素，绘制到前景层
    loadExistingMask() {
      if (!this.maskPath) return
      if (!this.$refs.canvasForeground || !this.ctxForeground) return
      const maskImg = new Image()
      maskImg.crossOrigin = 'Anonymous'
      maskImg.src = this.maskPath
      maskImg.onload = () => {
        try {
          const fg = this.$refs.canvasForeground
          // 通过临时 canvas 读取像素数据并提取黑色作为遮罩
          const tempCanvas = document.createElement('canvas')
          tempCanvas.width = fg.width
          tempCanvas.height = fg.height
          const tempCtx = tempCanvas.getContext('2d')
          tempCtx.drawImage(maskImg, 0, 0, fg.width, fg.height)
          const imgData = tempCtx.getImageData(0, 0, fg.width, fg.height)
          const data = imgData.data
          const threshold = 30 // 接近黑色判定阈值
          for (let i = 0; i < data.length; i += 4) {
            const r = data[i]
            const g = data[i + 1]
            const b = data[i + 2]
            if (r < threshold && g < threshold && b < threshold) {
              data[i] = 0
              data[i + 1] = 0
              data[i + 2] = 0
              data[i + 3] = 255
            } else {
              data[i + 3] = 0
            }
          }
          tempCtx.putImageData(imgData, 0, 0)
          // 确保为正常合成模式
          this.ctxForeground.globalCompositeOperation = 'source-over'
          this.ctxForeground.drawImage(tempCanvas, 0, 0)
          console.log('✅ 已回填上一次的遮罩')
        } catch (err) {
          console.error('遮罩回填失败（可能为跨域问题）:', err)
        }
      }
      maskImg.onerror = () => {
        console.error('遮罩图加载失败:', this.maskPath)
      }
    },
    startDrawing(e) {
      if (this.originalImage) {
        this.isDrawing = true
        const rect = this.$refs.canvasForeground.getBoundingClientRect()
        const scaleX = this.$refs.canvasForeground.width / rect.width
        const scaleY = this.$refs.canvasForeground.height / rect.height
        const x = Math.round((e.clientX - rect.left) * scaleX)
        const y = Math.round((e.clientY - rect.top) * scaleY)

        this.ctxForeground.beginPath()
        this.ctxForeground.moveTo(x, y)
      }
    },
    stopDrawing() {
      if (this.originalImage) {
        this.isDrawing = false
        this.ctxForeground.closePath()
      }
    },
    async saveDrawing() {
      if (this.originalImage) {
        // 创建一个临时canvas用于合并两个图层
        const tempCanvas = document.createElement('canvas')
        const canvasBackground = this.$refs.canvasBackground
        const canvasForeground = this.$refs.canvasForeground
        // 设置临时canvas的尺寸与原canvas一致
        tempCanvas.width = canvasBackground.width
        tempCanvas.height = canvasBackground.height
        const tempCtx = tempCanvas.getContext('2d')
        // 先绘制背景层（原图）
        tempCtx.drawImage(canvasBackground, 0, 0)
        // 再绘制前景层（遮罩层）
        tempCtx.drawImage(canvasForeground, 0, 0)
        // 获取合并后的图像数据
        const imageDataURL = tempCanvas.toDataURL('image/png') // PNG格式保留透明度
        this.currentImage = imageDataURL
        const filename = new Date().getTime() + '.png'
        // 转换为 Blob 对象
        fetch(imageDataURL)
          .then(res => res.blob())
          .then(blob => {
            // 创建文件对象
            const file = new File([blob], filename, { type: 'image/png' })
            this.maskImage(file)
          })
      }
    },
    clearImage() {
      if (this.$refs.canvasBackground && this.$refs.canvasForeground && this.ctxBackground && this.ctxForeground) {
        const canvasBackground = this.$refs.canvasBackground
        const canvasForeground = this.$refs.canvasForeground
        // 清空背景层
        this.ctxBackground.clearRect(0, 0, canvasBackground.width, canvasBackground.height)
        // 清空前景层
        this.ctxForeground.clearRect(0, 0, canvasForeground.width, canvasForeground.height)
        this.originalImage = null
      }
    },
    closeModal() {
      this.$emit('update:visibleCanvas', false)
    },
    // 消息通知栏
    open1(type = 'warning', color, notion) {
      notification(type)
      const h = this.$createElement
      this.$notify({
        title: '系统通知',
        type: type,
        message: h('i', { style: `color: ${color}` }, notion)
      })
    },
    // 上传图片到OSS
    async uploadToOSS(file, ossClient) {
      const tmp = '/input/'
      const fileName = file.name
      try {
        const result = await ossClient.put(tmp + fileName, file)
        return result.url
      } catch (err) {
        return null
      }
    },
    async maskImage(file) {
      this.loading = true
      const ossClient = await getOssClient()
      // 上传图片到OSS
      const imageUrl = await this.uploadToOSS(file, ossClient)
      console.log(imageUrl)
      if (imageUrl) {
        // 通过事件通知父组件更新遮罩路径（不能直接修改 prop）
        this.$emit('maskPath', imageUrl)
        this.loading = false
        this.open1('success', 'green', '已正确保存涂鸦内容,可以开始点击执行任务!')
        // 保存成功后关闭漂浮窗
        this.closeModal()
      }
    }
  },
  computed: {
    formattedBrushSize() {
      return this.brushSize < 10 ? '0' + this.brushSize : this.brushSize
    }
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeystart)
  }
}
</script>

<style lang="less" scoped>
@import "@/style/common.less";
@import "@/assets/fonts/style.css";
@import "@/assets/font_sczlgzino/iconfont.css";

* {
  padding: 0;
  margin: 0;
  box-sizing: border-box;
}

// 遮罩层动画
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// 遮罩层
.canvas-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.3s ease;
}

// 漂浮窗主体
.canvas-modal {
  width: 1400px;
  max-width: 90vw;
  height: 900px;
  max-height: 90vh;
  background: white;
  border-radius: 30px;
  box-shadow: 0 10px 50px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: fadeInUp 0.3s ease;
  position: relative;
  padding: 20px 30px 30px;
}

// 关闭按钮
.canvas-close-btn {
  position: absolute;
  top: 20px;
  right: 30px;
  width: 40px;
  height: 40px;
  background: #f8f9fa;
  color: #6c757d;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  cursor: pointer;
  transition: all 0.3s;
  z-index: 10;

  &:hover {
    background: #ff8c42;
    color: white;
    transform: scale(1.1);
  }
}

// 漂浮窗内容
.canvas-modal-body {
  flex: 1;
  padding: 10px 0 0;
  display: flex;
  flex-direction: column;
  gap: 25px;
  overflow: hidden;
}

// 画笔大小控制条（仿搜索栏样式）
.brush-size-bar {
  display: flex;
  align-items: center;
  gap: 20px;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: 15px 25px;
  background: white;
  border: 2px solid #dee2e6;
  border-radius: 30px;
  box-shadow: 0 2px 15px rgba(0, 0, 0, 0.08);
  transition: all 0.3s;

  &:hover {
    border-color: #ff8c42;
    box-shadow: 0 4px 20px rgba(255, 140, 66, 0.2);
  }

  .brush-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    font-weight: 500;
    color: #495057;
    white-space: nowrap;

    .brush-icon {
      font-size: 20px;
    }
  }

  .brush-slider {
    flex: 1;
    padding: 0 10px;
  }

  .brush-value {
    min-width: 60px;
    text-align: center;
    font-size: 16px;
    font-weight: 600;
    color: #ff8c42;
    padding: 5px 15px;
    background: #fff8f3;
    border-radius: 20px;
  }
}

// Element UI Slider 样式覆盖
::v-deep {
  .el-slider__runway {
    height: 8px;
    border-radius: 8px;
    background-color: #dee2e6;
  }

  .el-slider__bar {
    height: 8px;
    background-color: #ff8c42;
    border-radius: 8px;
  }

  .el-slider__button {
    width: 18px;
    height: 18px;
    background-color: #ff8c42;
    border: 3px solid white;
    box-shadow: 0 2px 8px rgba(255, 140, 66, 0.3);
    transition: all 0.3s;

    &:hover {
      transform: scale(1.2);
    }
  }
}

// 操作按钮组（仿筛选按钮样式）
.canvas-controls {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;

  .control-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 24px;
    background: #f8f9fa;
    color: #6c757d;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s;
    border: 1px solid #e5e7eb;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
    white-space: nowrap;

    .btn-icon {
      font-size: 16px;
    }

    &:hover {
      background: #fff8f3;
      color: #ff8c42;
      border-color: #ff8c42;
      box-shadow: 0 4px 15px rgba(255, 140, 66, 0.15);
      transform: translateY(-2px);
    }

    &.active {
      background: #ff8c42;
      color: white;
      border-color: #ff8c42;
      box-shadow: 0 4px 15px rgba(255, 140, 66, 0.3);
    }

    &.delete-btn {
      &:hover {
        background: #dc3545;
        color: white;
        border-color: #dc3545;
        box-shadow: 0 4px 15px rgba(220, 53, 69, 0.3);
      }
    }
  }
}

// 画布区域
.canvas-board {
  flex: 1;
  background: #f8f9fa;
  border: 2px solid #dee2e6;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: auto;
  position: relative;
  box-shadow: inset 0 2px 10px rgba(0, 0, 0, 0.05);

  // 滚动条样式
  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #ff8c42;
    border-radius: 4px;

    &:hover {
      background: #ff7a2e;
    }
  }

  canvas {
    max-width: 100%;
    max-height: 100%;
    cursor: none; // 隐藏默认光标
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  }

  // 背景层：显示原图
  .canvas-background {
    position: absolute;
    background: white;
    z-index: 1;
  }

  // 前景层：绘制遮罩
  .canvas-foreground {
    position: absolute;
    z-index: 2;
  }

  // 自定义画笔光标
  .brush-cursor {
    position: absolute;
    border: 2px solid #ff8c42;
    border-radius: 50%;
    pointer-events: none; // 不阻挡鼠标事件
    z-index: 1000;
    transition: width 0.1s ease, height 0.1s ease;
    box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.8),
                0 0 10px rgba(255, 140, 66, 0.3);
  }
}

// 底部操作按钮
.canvas-actions {
  display: flex;
  gap: 20px;
  justify-content: center;
  padding-top: 10px;

  button {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 14px 40px;
    border: none;
    border-radius: 30px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);

    .btn-icon {
      font-size: 18px;
    }

    &:active {
      transform: translateY(1px);
    }
  }

  .action-save-btn {
    background: #ff8c42;
    color: white;

    &:hover {
      background: #ff7a2e;
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(255, 140, 66, 0.4);
    }
  }

  .action-cancel-btn {
    background: white;
    color: #6c757d;
    border: 2px solid #dee2e6;

    &:hover {
      background: #f8f9fa;
      border-color: #adb5bd;
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
    }
  }
}
</style>
